import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  Spinner,
  Typography
} from "@material-tailwind/react";
import { LockClosedIcon } from "@heroicons/react/24/solid";
import { observer } from "mobx-react-lite";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe
} from "@stripe/react-stripe-js";
import { Appearance, loadStripe } from "@stripe/stripe-js";
import { StripeCardController } from "../Controllers/StripeCardController";
import { alertController } from "../../../Common/Controllers/AlertController";
import { utils } from "../../../Common/Controllers/Utils";

/** Stripe is loaded once, for this page */
const stripePromise = loadStripe(utils.stripePublicKey);

export const StripeCardForm = observer(() => {
  const stripeController = React.useMemo(
    () => new StripeCardController(),
    []
  );

  useEffect(() => {
    stripeController.init();
  }, [stripeController]);


  if (!stripeController.isInitialized) {
    return <div className="flex justify-center items-center text-gray-400 animate-fadeUp">
      <Spinner/>
    </div>;
  }

  const appearance: Appearance = {
    theme: "night",
    variables: {
      colorPrimary: "#1f2937",
      colorBackground: "#111827",
      colorText: "#ffffff",
      colorDanger: "#ef4444",
      fontFamily: "Roboto, system-ui, sans-serif",
      borderRadius: "0.5rem"
    }
  };

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret: stripeController.clientSecret,
        appearance
      }}
    >
      <StripePaymentInner controller={stripeController}/>
    </Elements>
  );
});

const StripePaymentInner = observer(
  ({controller}: { controller: StripeCardController }) => {
    const stripe = useStripe();
    const elements = useElements();

    const isReady = !!stripe && !!elements && controller.isInitialized;

    if (!isReady) {
      return <div className="flex justify-center items-center text-gray-400 animate-fadeUp">
        <Spinner/>
      </div>;
    }

    const onPayClick = async () => {
      controller.isLoading = true;

      if(!controller.isOrderValid){
        controller.isLoading = false;
        return;
      }

      await controller.startPayment();

      const result = await stripe.confirmPayment({
        elements,
        redirect: "if_required"
      });

      if (result.error) {
        alertController.displayAlert(
          "Payment failed",
          result.error.message ?? "Payment error",
          "error",
          4000
        );
        controller.isLoading = false;
        return;
      }

      await controller.finalizePayment();
      controller.isLoading = false;
    };

    return (
      <Card className="w-full max-w-[28rem] bg-gray-900">
        <CardBody className="bg-gray-900 p-6">
          <PaymentElement
            options={{
              layout: {
                type: "accordion",
                spacedAccordionItems: false
              }
            }}
          />

          <Button
            size="lg"
            color="blue-gray"
            className="mt-6"
            fullWidth
            disabled={!isReady || controller.isLoading}
            onClick={onPayClick}
          >
            {controller.isLoading ? <div className="flex justify-center items-center text-gray-400 animate-fadeUp">
              <Spinner/>
            </div> : "Pay now"}
          </Button>

          <Typography
            variant="small"
            color="white"
            className="mt-4 flex items-center justify-center gap-2 opacity-60"
          >
            <LockClosedIcon className="h-4 w-4"/>
            Payments are secure and encrypted
          </Typography>
        </CardBody>
      </Card>
    );
  }
);


